import { useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    Card,
    CardBody,
    Container,
    Row,
    Col,
    Button
} from "reactstrap";
import Header from 'components/Headers/Header';
import { getSpecialty } from 'config/specialtyConfig';

const Congrats = () => {
    const location = useLocation();
    const history = useHistory();
    const activeSpecialty = useSelector(state => state.specialty?.activeSpecialty || 'pmr');
    const purchasedSpecialty = location.state?.specialty || activeSpecialty;
    const specialtyConfig = getSpecialty(purchasedSpecialty);
    const specialtyName = specialtyConfig.name;
    const planName = location.state?.plan || `${specialtyName} Prep`;
    const isExtension = !!location.state?.isExtension;
    const isAddon = !!location.state?.isAddon;
    const isBundle = !!location.state?.isBundle;
    const bundleSpecialties = location.state?.bundleSpecialties || [];
    const newEndDate = location.state?.subscriptionEndDate;

    const formattedEndDate = (() => {
        if (!newEndDate) return null;
        try {
            return new Date(newEndDate).toLocaleDateString('en-US', {
                month: 'long', day: 'numeric', year: 'numeric',
            });
        } catch (e) {
            return null;
        }
    })();

    useEffect(() => {
        // Redirect to dashboard after 10 seconds
        const timer = setTimeout(() => {
            history.push('/client/question-bank/create-test');
        }, 10000);

        return () => clearTimeout(timer);
    }, [history]);

    return (
        <>
            <Header />
            <Container className="mt--7" fluid>
                <Row className="justify-content-center">
                    <Col lg="8" md="10">
                        <Card className="border-0 shadow">
                            <CardBody className="text-center py-5">
                                <div className="mb-4">
                                    <i
                                        className={isBundle ? 'fas fa-layer-group text-warning' : isAddon ? `${specialtyConfig.icon} text-success` : isExtension ? 'fas fa-calendar-check text-success' : 'fas fa-check-circle text-success'}
                                        style={{ fontSize: '80px' }}
                                    ></i>
                                </div>
                                <h1 className="display-3 text-dark mb-3">
                                    {isBundle
                                        ? 'Bundle Activated!'
                                        : isAddon
                                            ? `${specialtyName} Prep Added!`
                                            : isExtension
                                                ? 'Subscription Extended!'
                                                : `Welcome to ${specialtyName} Prep!`
                                    }
                                </h1>
                                <p className="lead text-muted mb-4">
                                    {isBundle
                                        ? <>Your <strong>{planName}</strong> bundle has been activated!</>
                                        : isAddon
                                            ? <><strong>{specialtyName}</strong> question bank has been added to your account with the <strong>{planName}</strong> plan.</>
                                            : isExtension
                                                ? <>Your <strong>{planName}</strong> has been added to your subscription.</>
                                                : <>Your subscription to <strong>{planName}</strong> has been confirmed.</>
                                    }
                                </p>
                                {isBundle && bundleSpecialties.length > 0 && (
                                    <div className="mb-4">
                                        <p className="text-success font-weight-bold mb-2">
                                            <i className="fas fa-check-circle mr-2"></i>
                                            Activated Specialties:
                                        </p>
                                        <div className="d-flex justify-content-center flex-wrap" style={{ gap: '8px' }}>
                                            {bundleSpecialties.map(specId => (
                                                <span key={specId} className={`badge badge-${getSpecialty(specId).color}`} style={{ fontSize: '1rem', padding: '8px 16px' }}>
                                                    <i className={`${getSpecialty(specId).icon} mr-1`}></i>
                                                    {getSpecialty(specId).name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {isAddon && !isBundle && (
                                    <p className="text-success font-weight-bold mb-4">
                                        <i className="fas fa-exchange-alt mr-2"></i>
                                        Switch to {specialtyName} using the specialty selector in the sidebar to get started.
                                    </p>
                                )}
                                {isExtension && formattedEndDate && (
                                    <p className="text-success font-weight-bold mb-4">
                                        <i className="fas fa-calendar-alt mr-2"></i>
                                        New access end date: {formattedEndDate}
                                    </p>
                                )}
                                <hr className="my-4" />
                                <p className="text-muted mb-4">
                                    {isExtension
                                        ? 'Thanks for continuing your preparation with us. Keep building on your progress — all your data, history, and performance analytics are right where you left them.'
                                        : 'You now have full access to our comprehensive question bank, performance analytics, and all premium features.'
                                    }
                                </p>
                                <div className="mb-4">
                                    <h4 className="text-dark">What's Next?</h4>
                                    <ul className="list-unstyled mt-3">
                                        {isExtension ? (
                                            <>
                                                <li className="py-2">
                                                    <i className="fas fa-arrow-right text-primary mr-2"></i>
                                                    Jump back into your practice tests
                                                </li>
                                                <li className="py-2">
                                                    <i className="fas fa-arrow-right text-primary mr-2"></i>
                                                    Review your performance analytics
                                                </li>
                                                <li className="py-2">
                                                    <i className="fas fa-arrow-right text-primary mr-2"></i>
                                                    View your billing details anytime
                                                </li>
                                            </>
                                        ) : (
                                            <>
                                                <li className="py-2">
                                                    <i className="fas fa-arrow-right text-primary mr-2"></i>
                                                    Create your first practice test
                                                </li>
                                                <li className="py-2">
                                                    <i className="fas fa-arrow-right text-primary mr-2"></i>
                                                    Explore the question bank by topic
                                                </li>
                                                <li className="py-2">
                                                    <i className="fas fa-arrow-right text-primary mr-2"></i>
                                                    Track your progress in the Performance section
                                                </li>
                                            </>
                                        )}
                                    </ul>
                                </div>
                                <Button
                                    color="success"
                                    size="lg"
                                    onClick={() => history.push('/client/question-bank/create-test')}
                                >
                                    <i className="fas fa-play mr-2"></i>
                                    {isExtension ? 'Continue Learning' : 'Start Learning'}
                                </Button>
                                <p className="text-muted mt-3 small">
                                    You will be redirected automatically in a few seconds...
                                </p>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default Congrats;
